import { Component, ReactNode } from 'react'

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    this.setState({ error, errorInfo })
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-gray-950 p-4">
          <div className="max-w-2xl w-full bg-red-950 border border-red-800 rounded-lg p-6">
            <div className="flex items-start gap-4 mb-4">
              <div className="flex-shrink-0 w-12 h-12 bg-red-900 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h1 className="text-xl font-bold text-red-400 mb-2">Something went wrong</h1>
                <p className="text-red-300 text-sm mb-4">
                  An unexpected error occurred in the application. This has been logged for investigation.
                </p>
                <div className="bg-red-900 rounded p-3 mb-4">
                  <p className="text-red-200 text-sm font-mono break-all">
                    {this.state.error.message}
                  </p>
                </div>
                <details className="mb-4">
                  <summary className="text-red-400 text-sm font-medium cursor-pointer hover:text-red-300 transition-colors">
                    View Stack Trace
                  </summary>
                  <div className="mt-2 bg-red-900 rounded p-3 max-h-64 overflow-y-auto">
                    <pre className="text-red-200 text-xs font-mono whitespace-pre-wrap break-all">
                      {this.state.error.stack}
                      {this.state.errorInfo?.componentStack && (
                        <>
                          {'\n\nComponent Stack:'}
                          {this.state.errorInfo.componentStack}
                        </>
                      )}
                    </pre>
                  </div>
                </details>
                <button
                  onClick={this.handleReset}
                  className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-md text-sm font-medium transition-colors"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
